import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const DemographicsForm = ({
    accessToken,
    onComplete,
    assessmentId = 'CPM-00001',
    formId = null
}) => {
    const [fields, setFields] = useState([]);
    const [formData, setFormData] = useState({});
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState({});
    const [apiError, setApiError] = useState(null);
    const [currentFormId, setCurrentFormId] = useState(formId);
    const navigate = useNavigate();

    useEffect(() => {
        console.log(`📋 DemographicsForm initialized with assessmentId: ${assessmentId}`);
        console.log(`📋 Form ID from session: ${formId}`);
        fetchFormFields();
    }, [assessmentId, formId]);

    // ============================================
    // FETCH FORM FIELDS
    // ============================================
    const fetchFormFields = async () => {
        try {
            // If we have a form_id from the session, use it to fetch fields
            if (formId) {
                console.log(`📋 Fetching demographics fields for form_id: ${formId}`);
                const response = await api.get(`/candidates/form-id/${formId}`);
                console.log('✅ Form fields response:', response.data);

                if (response.data.success && response.data.fields) {
                    setFields(response.data.fields);
                    setCurrentFormId(formId);

                    const initialData = {};
                    response.data.fields.forEach(field => {
                        initialData[field.field_key] = '';
                    });
                    initialData.form_id = formId;
                    setFormData(initialData);
                    setLoading(false);
                    return;
                }
            }

            // Fallback: fetch by assessment ID
            console.log(`📋 Fetching demographics fields for assessment: ${assessmentId}`);
            const response = await api.get(`/candidates/form/${assessmentId}`);
            console.log('✅ Form fields response:', response.data);

            const fetchedFormId = response.data.form_id || formId || `${assessmentId.split('-')[0]}-00078`;
            setCurrentFormId(fetchedFormId);

            if (response.data.success && response.data.fields) {
                const uniqueFields = [];
                const seenKeys = new Set();

                response.data.fields.forEach(field => {
                    if (field.field_key === 'Any other' && seenKeys.has('Any other')) {
                        return;
                    }
                    if (!seenKeys.has(field.field_key)) {
                        seenKeys.add(field.field_key);
                        uniqueFields.push(field);
                    }
                });

                setFields(uniqueFields);

                const initialData = {};
                uniqueFields.forEach(field => {
                    initialData[field.field_key] = '';
                });
                initialData.form_id = fetchedFormId;
                setFormData(initialData);
            } else {
                console.log('⚠️ No fields found, using defaults');
                const defaultFields = getDefaultFields();
                setFields(defaultFields);
                const initialData = {};
                defaultFields.forEach(field => {
                    initialData[field.field_key] = '';
                });
                initialData.form_id = fetchedFormId;
                setFormData(initialData);
            }
            setLoading(false);
        } catch (error) {
            console.error('❌ Error fetching form fields:', error);
            const defaultFormId = formId || `${assessmentId.split('-')[0]}-00078`;
            setCurrentFormId(defaultFormId);
            const defaultFields = getDefaultFields();
            setFields(defaultFields);
            const initialData = {};
            defaultFields.forEach(field => {
                initialData[field.field_key] = '';
            });
            initialData.form_id = defaultFormId;
            setFormData(initialData);
            setLoading(false);
        }
    };

    // ============================================
    // GET DEFAULT FIELDS
    // ============================================
    const getDefaultFields = () => {
        const isCPM = assessmentId === 'CPM-00001';
        const isMIPS = assessmentId === 'MIPS-00001';
        const isGHQ = assessmentId === 'GHQ-001';

        let fields = [
            { id: 1, field_key: 'full_name', field_label: 'Full Name', field_type: 'Text', required: true },
            { id: 2, field_key: 'date_of_birth', field_label: 'Date of Birth', field_type: 'Date', required: true },
        ];

        if (isCPM) {
            fields.push(
                { id: 3, field_key: 'education', field_label: 'Education Level', field_type: 'Text', required: true },
                { id: 4, field_key: 'school', field_label: 'School Name', field_type: 'Text', required: false },
                { id: 5, field_key: 'grade', field_label: 'Grade', field_type: 'Text', required: false }
            );
        } else if (isMIPS) {
            fields.push(
                { id: 3, field_key: 'education', field_label: 'Education', field_type: 'Text', required: true },
                { id: 4, field_key: 'job_position', field_label: 'Job Position', field_type: 'Text', required: false },
                { id: 5, field_key: 'department', field_label: 'Department', field_type: 'Text', required: false }
            );
        } else {
            fields.push(
                { id: 3, field_key: 'education', field_label: 'Education', field_type: 'Text', required: false },
                { id: 4, field_key: 'job_position', field_label: 'Job Position', field_type: 'Text', required: false },
                { id: 5, field_key: 'department', field_label: 'Department', field_type: 'Text', required: false }
            );
        }

        fields.push(
            { id: 6, field_key: 'sex', field_label: 'Sex', field_type: 'Text', required: false },
            { id: 7, field_key: 'address', field_label: 'Address', field_type: 'Text', required: false },
            { id: 8, field_key: 'email', field_label: 'Email', field_type: 'Email', required: false },
            { id: 9, field_key: 'phone', field_label: 'Phone', field_type: 'Phone', required: false },
            { id: 10, field_key: 'data_usage_consent', field_label: 'Data Usage Consent', field_type: 'Consent Toggle', required: false }
        );

        return fields;
    };

    // ============================================
    // HANDLE INPUT CHANGE
    // ============================================
    const handleInputChange = (fieldKey, value) => {
        setFormData(prev => ({ ...prev, [fieldKey]: value }));
        if (errors[fieldKey]) {
            setErrors(prev => ({ ...prev, [fieldKey]: null }));
        }
        setApiError(null);
    };

    // ============================================
    // RENDER FIELD
    // ============================================
    const renderField = (field) => {
        const value = formData[field.field_key] || '';
        const isRequired = field.required;
        const hasError = errors[field.field_key];
        const uniqueKey = `${field.field_key}_${field.id || Math.random()}`;

        const baseClasses = `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#ff9f2f] focus:border-[#ff9f2f] transition ${hasError ? 'border-red-500' : 'border-gray-300'
            }`;

        switch (field.field_type) {
            case 'Date':
                return (
                    <div key={uniqueKey} className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {field.field_label}
                            {isRequired && <span className="text-red-500 ml-1">*</span>}
                        </label>
                        <input
                            type="date"
                            value={value}
                            onChange={(e) => handleInputChange(field.field_key, e.target.value)}
                            className={baseClasses}
                            required={isRequired}
                        />
                        {hasError && <p className="text-red-500 text-xs mt-1">{hasError}</p>}
                    </div>
                );

            case 'Email':
                return (
                    <div key={uniqueKey} className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {field.field_label}
                            {isRequired && <span className="text-red-500 ml-1">*</span>}
                        </label>
                        <input
                            type="email"
                            value={value}
                            onChange={(e) => handleInputChange(field.field_key, e.target.value)}
                            className={baseClasses}
                            placeholder={`Enter ${field.field_label.toLowerCase()}`}
                            required={isRequired}
                        />
                        {hasError && <p className="text-red-500 text-xs mt-1">{hasError}</p>}
                    </div>
                );

            case 'Phone':
                return (
                    <div key={uniqueKey} className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {field.field_label}
                            {isRequired && <span className="text-red-500 ml-1">*</span>}
                        </label>
                        <input
                            type="tel"
                            value={value}
                            onChange={(e) => handleInputChange(field.field_key, e.target.value)}
                            className={baseClasses}
                            placeholder="Enter phone number"
                            required={isRequired}
                        />
                        {hasError && <p className="text-red-500 text-xs mt-1">{hasError}</p>}
                    </div>
                );

            case 'Consent Toggle':
                return (
                    <div key={uniqueKey} className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex items-start">
                            <input
                                type="checkbox"
                                checked={value === true || value === 'Yes' || value === 'on'}
                                onChange={(e) => handleInputChange(field.field_key, e.target.checked)}
                                className="mt-1 mr-3 h-4 w-4 text-[#ff9f2f] focus:ring-[#ff9f2f] border-gray-300 rounded"
                            />
                            <div>
                                <label className="block text-sm font-medium text-gray-700">
                                    {field.field_label}
                                    {isRequired && <span className="text-red-500 ml-1">*</span>}
                                </label>
                                <p className="text-xs text-gray-500">
                                    By checking this box, you agree to our data usage policy.
                                </p>
                            </div>
                        </div>
                        {hasError && <p className="text-red-500 text-xs mt-1">{hasError}</p>}
                    </div>
                );

            default:
                return (
                    <div key={uniqueKey} className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {field.field_label}
                            {isRequired && <span className="text-red-500 ml-1">*</span>}
                        </label>
                        <input
                            type="text"
                            value={value}
                            onChange={(e) => handleInputChange(field.field_key, e.target.value)}
                            className={baseClasses}
                            placeholder={`Enter ${field.field_label.toLowerCase()}`}
                            required={isRequired}
                        />
                        {hasError && <p className="text-red-500 text-xs mt-1">{hasError}</p>}
                    </div>
                );
        }
    };

    // ============================================
    // VALIDATE FORM
    // ============================================
    const validateForm = () => {
        const newErrors = {};
        let isValid = true;

        fields.forEach(field => {
            if (field.required) {
                const value = formData[field.field_key];
                if (!value || (typeof value === 'string' && value.trim() === '')) {
                    newErrors[field.field_key] = `${field.field_label} is required`;
                    isValid = false;
                }
            }
        });

        setErrors(newErrors);
        return isValid;
    };

    // ============================================
    // HANDLE SUBMIT - DEFINED HERE
    // ============================================
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setSubmitting(true);
        setApiError(null);

        try {
            // Use the form ID from the session
            const submitData = {
                ...formData,
                form_id: currentFormId || formId || `${assessmentId.split('-')[0]}-00078`
            };

            console.log('📤 Saving demographics for token:', accessToken?.substring(0, 20) + '...');
            console.log('📋 Form data:', JSON.stringify(submitData, null, 2));
            console.log('📋 Assessment ID:', assessmentId);
            console.log('📋 Form ID:', submitData.form_id);

            const response = await api.post(`/candidates/${accessToken}/demographics`, submitData);

            console.log('✅ Response:', response.data);

            if (response.data.success) {
                if (response.data.candidate) {
                    localStorage.setItem('candidateData', JSON.stringify(response.data.candidate));
                }
                onComplete?.(response.data.candidate);
            } else {
                if (response.data.missing) {
                    const newErrors = {};
                    response.data.missing.forEach(field => {
                        newErrors[field] = 'This field is required';
                    });
                    setErrors(newErrors);
                }
                setApiError(response.data.error || 'Failed to save demographics');
            }
        } catch (error) {
            console.error('❌ Error submitting form:', error);

            if (error.response) {
                console.error('Response status:', error.response.status);
                console.error('Response data:', error.response.data);

                if (typeof error.response.data === 'string' && error.response.data.includes('<!DOCTYPE')) {
                    setApiError('Server returned HTML instead of JSON. Please check if the API endpoint is correct.');
                } else {
                    setApiError(error.response.data?.error || `Server error: ${error.response.status}`);
                }
            } else if (error.request) {
                console.error('No response received:', error.request);
                setApiError('No response from server. Please check if the server is running.');
            } else {
                setApiError(error.message || 'Failed to submit form. Please try again.');
            }
        } finally {
            setSubmitting(false);
        }
    };

    // ============================================
    // RENDER
    // ============================================
    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#ff9f2f]"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#f4f6fb] py-12">
            <div className="max-w-2xl mx-auto px-4">
                <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-200">
                    <div className="bg-[#063b56] px-6 py-6">
                        <h1 className="text-2xl font-bold text-white">
                            Personal Information
                        </h1>
                        <p className="text-gray-300 text-sm">
                            Please fill in your details before starting the assessment
                        </p>
                        <p className="text-gray-400 text-xs mt-1">
                            Assessment: {assessmentId}
                        </p>
                        {currentFormId && (
                            <p className="text-gray-400 text-xs">
                                Form: {currentFormId}
                            </p>
                        )}
                    </div>

                    <form onSubmit={handleSubmit} className="p-6">
                        {apiError && (
                            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                                <strong>Error:</strong> {apiError}
                            </div>
                        )}

                        <div className="space-y-2">
                            {fields.map(field => renderField(field))}
                        </div>

                        <div className="mt-6 pt-4 border-t border-gray-200">
                            <div className="flex items-center justify-between">
                                <div className="text-sm text-gray-500">
                                    <span className="text-red-500">*</span> Required fields
                                </div>
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="bg-[#ff9f2f] text-white px-6 py-2 rounded-lg hover:bg-[#e68a1a] disabled:opacity-50 flex items-center shadow-sm"
                                >
                                    {submitting ? (
                                        <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                            Saving...
                                        </>
                                    ) : (
                                        'Continue to Assessment →'
                                    )}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>

                <div className="mt-4 text-center text-xs text-gray-400">
                    Your information is secure and will only be used for assessment purposes.
                    <br />
                    Assessment ID: {assessmentId}
                    {currentFormId && <br />}Form: {currentFormId}
                </div>
            </div>
        </div>
    );
};

export default DemographicsForm;